import { UserCreateInput } from "@repo/types";
import { Alert, AlertDescription } from "@repo/ui/components/ui/alert";
import { Badge } from "@repo/ui/components/ui/badge";
import { Button } from "@repo/ui/components/ui/button";
import { useCreateUser } from "@repo/ui/hooks/api/user";
import { cn } from "@repo/ui/lib/utils";
import {
  AlertCircle,
  ArrowRight,
  CheckCircle2,
  Loader2,
  Plus,
  SkipForward,
  UserPlus,
  Users,
} from "lucide-react";
import { useRef, useState } from "react";
import {
  UserMiniForm,
  type UserMiniFormHandle,
} from "../components/user-mini-form";
import type { UserFormSchema } from "../../user/hooks/user-form-schema";

interface CreatedGroupData {
  id: number;
  name: string;
  groupe_type: string;
  licence: number;
  plan_type?: string;
  max_parcours_per_month?: number;
  tier_id?: string;
}

interface CreatedUserData {
  id: number;
  username: string;
  email: string;
  profil_name?: string;
}

interface StepUtilisateursProps {
  createdGroup: CreatedGroupData;
  onUsersCreated: (users: CreatedUserData[]) => void;
  onSkip: () => void;
}

const MAX_USERS = 5;

export function StepUtilisateurs({
  createdGroup,
  onUsersCreated,
  onSkip,
}: StepUtilisateursProps) {
  const [formCount, setFormCount] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [partialResults, setPartialResults] = useState<
    { email: string; success: boolean; error?: string }[]
  >([]);

  const formRefs = useRef<(UserMiniFormHandle | null)[]>([]);
  const createUser = useCreateUser();

  const addUser = () => {
    if (formCount < MAX_USERS) {
      setFormCount((c) => c + 1);
      // Extend refs array
      formRefs.current = [...formRefs.current];
    }
  };

  const removeUser = (index: number) => {
    setFormCount((c) => {
      const next = c - 1;
      formRefs.current = formRefs.current.filter((_, i) => i !== index);
      return next;
    });
  };

  const handleCreate = async () => {
    setError(null);
    setPartialResults([]);

    // Validate all forms
    const validationResults = await Promise.all(
      Array.from({ length: formCount }, (_, i) =>
        formRefs.current[i]?.validate() ?? Promise.resolve(false)
      )
    );

    if (validationResults.some((v) => !v)) {
      setError(
        "Certains formulaires contiennent des erreurs. Veuillez les corriger avant de continuer."
      );
      return;
    }

    // Collect data from all forms
    const formsData: UserFormSchema[] = Array.from(
      { length: formCount },
      (_, i) => formRefs.current[i]!.getData()
    );

    setIsSubmitting(true);

    // Create users sequentially to give better error feedback
    const results: CreatedUserData[] = [];
    const partial: { email: string; success: boolean; error?: string }[] = [];

    for (const formData of formsData) {
      try {
        const payload: UserCreateInput = {
          username: formData.username,
          email: formData.email,
          password: formData.password!,
          profil_id: formData.profil_id,
          groupe_id: createdGroup.id,
          is_active: formData.is_active ?? true,
          email_active: formData.email_active ?? false,
        } as unknown as UserCreateInput;

        const result = await createUser.mutateAsync(payload);
        const data = result.data as any;

        results.push({
          id: data.id,
          username: data.username ?? formData.username,
          email: data.email ?? formData.email,
          profil_name: data.profil?.name ?? undefined,
        });
        partial.push({ email: formData.email, success: true });
      } catch (err: any) {
        const msg =
          err?.response?.data?.message ??
          `Erreur lors de la création de ${formData.email}`;
        partial.push({ email: formData.email, success: false, error: msg });
      }
    }

    setIsSubmitting(false);
    setPartialResults(partial);

    const failedCount = partial.filter((p) => !p.success).length;

    if (failedCount === 0) {
      // All succeeded — advance wizard
      onUsersCreated(results);
    } else if (results.length > 0) {
      // Partial success — still advance, but show warning
      onUsersCreated(results);
    } else {
      // All failed
      setError(
        "Tous les utilisateurs ont échoué. Vérifiez les données et réessayez."
      );
    }
  };

  return (
    <div className="space-y-6">
      {/* Success banner — group was created */}
      <div className="flex items-center gap-3 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3">
        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-100 shrink-0">
          <CheckCircle2 className="h-4 w-4 text-emerald-600" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-emerald-800 truncate">
            Groupe «&nbsp;{createdGroup.name}&nbsp;» créé avec succès
          </p>
          <p className="text-xs text-emerald-600 mt-0.5">
            ID #{createdGroup.id} ·{" "}
            <span className="font-medium">{createdGroup.groupe_type}</span>
            {createdGroup.plan_type && (
              <> · Plan {createdGroup.plan_type}</>
            )}
            {createdGroup.max_parcours_per_month && (
              <> · {createdGroup.max_parcours_per_month} parcours/mois</>
            )}
            {" "}· {createdGroup.licence} licence
            {createdGroup.licence > 1 ? "s" : ""}
          </p>
        </div>
      </div>

      {/* Section header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-primary" />
          <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Utilisateurs à créer
          </h3>
          <Badge variant="secondary" className="text-xs">
            {formCount} / {MAX_USERS}
          </Badge>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="gap-1.5 text-xs h-8"
          onClick={addUser}
          disabled={formCount >= MAX_USERS}
        >
          <Plus className="h-3.5 w-3.5" />
          Ajouter
        </Button>
      </div>

      {/* Error */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Partial results feedback */}
      {partialResults.length > 0 && (
        <div className="space-y-1.5">
          {partialResults.map((r, i) => (
            <div
              key={i}
              className={cn(
                "flex items-center gap-2 rounded-lg px-3 py-2 text-xs",
                r.success
                  ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                  : "bg-destructive/5 text-destructive border border-destructive/20"
              )}
            >
              {r.success ? (
                <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
              ) : (
                <AlertCircle className="h-3.5 w-3.5 shrink-0" />
              )}
              <span className="font-medium">{r.email}</span>
              {r.error && <span className="ml-1 opacity-80">— {r.error}</span>}
            </div>
          ))}
        </div>
      )}

      {/* User forms */}
      <div className="space-y-3">
        {Array.from({ length: formCount }, (_, i) => (
          <UserMiniForm
            key={i}
            index={i}
            groupeId={createdGroup.id}
            showRemove={formCount > 1}
            onRemove={() => removeUser(i)}
            ref={(el) => {
              formRefs.current[i] = el;
            }}
          />
        ))}
      </div>

      {/* Add more hint */}
      {formCount < MAX_USERS && (
        <button
          type="button"
          onClick={addUser}
          className="w-full rounded-xl border-2 border-dashed border-border/60 py-3 text-sm text-muted-foreground hover:border-primary/40 hover:text-primary hover:bg-primary/3 transition-all duration-150 flex items-center justify-center gap-2 group"
        >
          <UserPlus className="h-4 w-4 group-hover:scale-110 transition-transform" />
          Ajouter un utilisateur
        </button>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between pt-2 border-t border-border/40">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="gap-1.5 text-muted-foreground hover:text-foreground"
          onClick={onSkip}
          disabled={isSubmitting}
        >
          <SkipForward className="h-4 w-4" />
          Passer cette étape
        </Button>

        <Button
          type="button"
          size="lg"
          className="min-w-[220px] gap-2"
          onClick={handleCreate}
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Création en cours...
            </>
          ) : (
            <>
              Créer les utilisateurs
              <ArrowRight className="h-4 w-4" />
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
